import { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import BreakdownCoverTable from '@acorn-portal/shared/src/components/table/breakdown-cover-table';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import useAddonActions from '@acorn-portal/shared/src/hooks/useAddonActions';
import useOptionalExtras from '@acorn-portal/shared/src/hooks/useOptionalExtras';
import ExcessReimbursement from '@acorn-portal/shared/src/components/table/excess-reimbursement';

import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useNavigate } from 'react-router';
import { ArrowForward } from '@mui/icons-material';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import PaymentOptionsSummary from './paymentOptionSummary/PaymentOptionsSummary';
import NoClaimsDiscountCard from '@acorn-portal/shared/src/components/renewal/NoClaimsDiscountCard';
import TaxiHire from '@acorn-portal/shared/src/components/renewal/TaxiHire';
import useReferenceData from '@acorn-portal/shared/src/hooks/useReferenceData';
import { ListNames } from '@acorn-portal/shared/src/store/referenceData.slice';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import OptionalExtra from '@acorn-portal/shared/src/components/table/optional-extras';

const styles = {
	maxWidth: 900,
	marginY: 2,
	marginLeft: 'auto',
	marginRight: 'auto',
};

const OptionalExtrasSection = () => {
	const { getReferenceDataValue } = useReferenceData();
	const { specialExtras, optionalExtras } = useOptionalExtras();
	const { onAddSingleAddOn, onRemoveAddon, onReplaceAddon, isAddOnLoading } = useAddonActions();
	const renewalStore = useAppSelector((redux) => redux.renewal);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const isShortTermPolicy = fullPolicy.isShortTermPolicy;

	const isPublicOrPrivateHire = ['Private Hire', 'Public Hire'].includes(
		getReferenceDataValue(
			ListNames.noClaimsDiscountTypes,
			renewalStore.renewalRatingResponse?.coverDetails?.ncbDetails.ncdEntitlement ?? '',
		),
	);
	const isNcbProtectionAvailable = useMemo(() => {
		const ncbDetails = renewalStore.renewalRatingResponse?.coverDetails?.ncbDetails;
		const ncbYears = Number(ncbDetails?.ncbYears ?? 0);
		const ncdEntitlement = ncbDetails?.ncdEntitlement;

		if (!ncbYears || ncbYears < 1 || !ncdEntitlement) return false;

		return fullPolicy.product === Product.TAXI ? isPublicOrPrivateHire : true;
	}, [renewalStore.renewalRatingResponse?.coverDetails?.ncbDetails, fullPolicy.product, isPublicOrPrivateHire]);

	const addOns = renewalStore.renewalRatingResponse?.addOns;

	const liabilityOptions = useMemo(
		() => renewalStore && addOns?.filter((x) => x.addOnDescription.indexOf('Liability') !== -1),
		[addOns, renewalStore],
	);

	const navigate = useNavigate();

	const hasExtras = [
		specialExtras.excessReimbursement.length,
		specialExtras.keycare.length,
		optionalExtras.length,
		liabilityOptions?.length,
	].some((length) => (length ?? 0) > 0);

	return (
		<>
			{renewalStore.renewalRatingResponse && (
				<Box sx={styles}>
					<PaymentOptionsSummary
						paymentOptions={renewalStore.renewalRatingResponse}
						value={renewalStore.paymentOptions}
					/>
				</Box>
			)}
			{hasExtras && (
				<Box
					sx={(theme) => ({
						...styles,
						paddingBottom: 2,
						borderRadius: 2,
						overflow: 'hidden',
						border: `2px solid ${theme.palette.grey[300]}`,
						[theme.breakpoints.down('md')]: {
							width: '100%',
						},
					})}
				>
					{specialExtras.excessReimbursement.length > 0 && (
						<ExcessReimbursement
							addOns={specialExtras.excessReimbursement}
							onSelect={onReplaceAddon}
							onRemove={onRemoveAddon}
						/>
					)}
					{specialExtras.keycare.length > 0 && (
						<OptionalExtra
							option={specialExtras.keycare[0]}
							onSelect={onAddSingleAddOn}
							onRemove={onRemoveAddon}
							renderDescription={({ description }) => (
								<>
									<Typography sx={{ mb: 1, fontWeight: 'bold' }}>{description}</Typography>{' '}
									<Typography sx={{ mb: 2, fontSize: '1rem' }}>
										Cover for lost, stolen or broken keys up to &pound;1500. Standalone insurance which means it
										won&apos;t effect your no claims bonus and there is no excess to pay.
									</Typography>
								</>
							)}
						/>
					)}
					{liabilityOptions?.map((option, index) => {
						const liabilityIndex = liabilityOptions.findIndex((e) => e.addOnCode.includes('LIABILITY'));

						const liabilityWordingText = (
							<Typography
								sx={{
									pl: 2,
									mb: 2,
									fontSize: '1rem',
									fontWeight: 'bold',
								}}
							>
								Please note that as standard your policy benefits include £5 million public liability cover for up to 5
								passengers. The additional cover listed below are available as a top-up product and would be in addition
								to this benefit.
							</Typography>
						);

						const disabled =
							(renewalStore.addons?.some((addon) => addon.addOnCode === 'LIABILITY_5_7') &&
								option.addOnCode === 'LIABILITY_10') ||
							(renewalStore.addons?.some((addon) => addon.addOnCode === 'LIABILITY_10') &&
								option.addOnCode === 'LIABILITY_5_7');

						return (
							<>
								{index === liabilityIndex && !isShortTermPolicy && liabilityWordingText}
								<OptionalExtra
									disabled={disabled}
									key={option.underwrittenDataPointId}
									option={option}
									onSelect={onAddSingleAddOn}
									onRemove={onRemoveAddon}
									renderDescription={({ description }) => (
										<>
											<Typography sx={{ mb: 1, fontWeight: 'bold' }}>{description}</Typography>{' '}
										</>
									)}
								/>
							</>
						);
					})}
				</Box>
			)}
			{specialExtras.breakdownCover.length > 0 && (
				<Box sx={styles}>
					<BreakdownCoverTable
						option={specialExtras.breakdownCover}
						onSelect={onAddSingleAddOn}
						onRemove={onRemoveAddon}
					/>
				</Box>
			)}
			{specialExtras.guaranteedReplacementHire.length > 0 && (
				<Box sx={styles}>
					<TaxiHire
						option={specialExtras.guaranteedReplacementHire}
						onSelect={onReplaceAddon}
						onRemove={onRemoveAddon}
					/>
				</Box>
			)}
			{isNcbProtectionAvailable && (
				<Box sx={styles}>
					<NoClaimsDiscountCard />
				</Box>
			)}

			<Box display='flex' flexDirection='row-reverse' mx={{ sm: 0, md: 3 }}>
				<Button
					onClick={() => {
						navigate(ROUTE_PATHS.renewalCheckout);
					}}
					sx={(theme) => ({
						[theme.breakpoints.down('md')]: {
							width: '100%',
						},
						width: '40%',
					})}
					variant='contained'
					color='secondary'
					type='submit'
					size='large'
					endIcon={<ArrowForward />}
				>
					Pay Now
				</Button>
			</Box>

			<LoadingModal open={isAddOnLoading} message='Updating Your Renewal Quote...' />
		</>
	);
};

export default OptionalExtrasSection;
